/**
 * Captures how a visitor arrived (utm tags, ad-click ids, referrer) once per app
 * entry, so "who's coming from where" is queryable in PostHog alongside the
 * referral funnel. First-touch values are pinned as person properties so they
 * survive the anonymous -> identified transition.
 *
 * Referral links carry `utm_source=referral` (see referral/referralLink.ts), so
 * referrals show up here as one acquisition source while the invite code adds
 * per-inviter granularity on top. The two systems share this capture layer.
 *
 * Web-first: web reads window.location + document.referrer; native reads the
 * initial deep-link URL. Native installs that come through the App/Play Store do
 * not carry these tags without deferred deep linking (a documented follow-up),
 * so on native this only fires for direct deep-link entries.
 */
import { useEffect, useRef } from "react";
import { Platform } from "react-native";
import * as Linking from "expo-linking";
import { usePostHog } from "posthog-react-native";

/** Query keys that describe where a visitor came from. */
const ACQUISITION_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "ref",
  "gclid",
  "fbclid",
] as const;

type AcquisitionParams = Partial<
  Record<(typeof ACQUISITION_KEYS)[number], string>
>;

function pickAcquisitionParams(
  params: Record<string, string | undefined>,
): AcquisitionParams {
  const picked: AcquisitionParams = {};
  for (const key of ACQUISITION_KEYS) {
    const value = params[key];
    if (typeof value === "string" && value.length > 0) {
      picked[key] = value;
    }
  }
  return picked;
}

export function useAcquisitionCapture() {
  const posthog = usePostHog();
  const captured = useRef(false);

  useEffect(() => {
    if (captured.current) return;
    let cancelled = false;

    const capture = (
      params: AcquisitionParams,
      referrer: string | null,
      entryPath: string | null,
    ) => {
      if (captured.current || cancelled) return;
      const hasSignal = Object.keys(params).length > 0 || !!referrer;
      if (!hasSignal) return;
      captured.current = true;

      posthog.capture("acquisition_source_captured", {
        ...params,
        referrer: referrer ?? null,
        entry_path: entryPath ?? null,
        $set: {
          last_utm_source: params.utm_source ?? null,
          last_utm_medium: params.utm_medium ?? null,
          last_utm_campaign: params.utm_campaign ?? null,
        },
        // First touch wins and sticks for the life of the person profile.
        $set_once: {
          initial_utm_source: params.utm_source ?? null,
          initial_utm_medium: params.utm_medium ?? null,
          initial_utm_campaign: params.utm_campaign ?? null,
          initial_referrer: referrer ?? null,
        },
      });
    };

    if (Platform.OS === "web" && typeof window !== "undefined") {
      const search = new URLSearchParams(window.location.search);
      const params: Record<string, string> = {};
      search.forEach((value, key) => {
        params[key] = value;
      });
      const referrer =
        typeof document !== "undefined" && document.referrer
          ? document.referrer
          : null;
      capture(
        pickAcquisitionParams(params),
        referrer,
        window.location.pathname,
      );
    } else {
      Linking.getInitialURL()
        .then((url) => {
          if (!url || cancelled) return;
          const parsed = Linking.parse(url);
          const params: Record<string, string> = {};
          for (const [key, value] of Object.entries(parsed.queryParams ?? {})) {
            if (typeof value === "string") params[key] = value;
          }
          capture(pickAcquisitionParams(params), null, parsed.path ?? null);
        })
        .catch(() => {
          // No initial URL / parse failure — nothing to attribute.
        });
    }

    return () => {
      cancelled = true;
    };
  }, [posthog]);
}
